import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { SearchProvider } from "./context/SearchContext";
import { NotificationsProvider } from "./context/NotificationsContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { GlossaryProvider } from "./context/GlossaryContext";
import { CryptoStreamProvider } from "./context/CryptoStreamContext";
import OnboardingTour from "./components/OnboardingTour";
import LiveTickerBar from "./components/LiveTickerBar";
import BottomNav from "./components/BottomNav";
import Landing from "./pages/Landing";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import AssetDetail from "./pages/AssetDetail";
import Watchlist from "./pages/Watchlist";
import Learn from "./pages/Learn";
import PaperTrading from "./pages/PaperTrading";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Settings from "./pages/Settings";
import PWAInstallBanner from "./components/PWAInstallBanner";
import NativeBootstrap from "./components/NativeBootstrap";
import Alerts from "./pages/Alerts";
import Screener from "./pages/Screener";
import Compare from "./pages/Compare";
import Wheel from "./pages/Wheel";
import Broker from "./pages/Broker";
import ChartSandbox from "./pages/ChartSandbox";
import LegalPage from "./pages/LegalPage";
import FooterDisclaimer from "./components/FooterDisclaimer";
import ErrorBoundary from "./components/ErrorBoundary";
import "@/App.css";

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/learn" element={<Learn />} />
      <Route path="/asset/:type/:symbol" element={<AssetDetail />} />
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/watchlist" element={<PrivateRoute><Watchlist /></PrivateRoute>} />
      <Route path="/paper" element={<PrivateRoute><PaperTrading /></PrivateRoute>} />
      <Route path="/alerts" element={<PrivateRoute><Alerts /></PrivateRoute>} />
      <Route path="/screener" element={<Screener />} />
      <Route path="/compare" element={<Compare />} />
      <Route path="/wheel" element={<PrivateRoute><Wheel /></PrivateRoute>} />
      <Route path="/broker" element={<PrivateRoute><Broker /></PrivateRoute>} />
      <Route path="/sandbox" element={<ChartSandbox />} />
      <Route path="/legal/terms" element={<LegalPage kind="terms" />} />
      <Route path="/legal/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/legal/risk" element={<LegalPage kind="risk" />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <NotificationsProvider>
                <OnboardingProvider>
                  <GlossaryProvider>
                    <CryptoStreamProvider>
                      <SearchProvider>
                        <AppRoutes />
                        <FooterDisclaimer />
                        <OnboardingTour />
                        <LiveTickerBar />
                        <BottomNav />
                        <PWAInstallBanner />
                        <NativeBootstrap />
                        <Toaster
                          position="top-right"
                          toastOptions={{
                            style: {
                              background: "var(--bg-surface)",
                              color: "var(--text-primary)",
                              border: "1px solid var(--border)",
                              borderRadius: "12px",
                              fontFamily: "IBM Plex Sans, sans-serif",
                            },
                          }}
                        />
                      </SearchProvider>
                    </CryptoStreamProvider>
                  </GlossaryProvider>
                </OnboardingProvider>
              </NotificationsProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
